import React from 'react';
import { useTranslation } from 'react-i18next';
import { useConnectionHealth } from '../../contexts/ConnectionHealthContext';

const DOT_COLORS = {
  connected: 'bg-green-500',
  degraded: 'bg-yellow-500',
  disconnected: 'bg-red-500',
};

const COMPONENT_LABEL_KEYS = {
  mongo: 'ui:connectionHealth.components.mongo',
  mqtt: 'ui:connectionHealth.components.mqtt',
  redis: 'ui:connectionHealth.components.redis',
  rabbitmq: 'ui:connectionHealth.components.rabbitmq',
  tagSocket: 'ui:connectionHealth.components.tagSocket',
};

function buildTooltip(t, { status, components, tagSocketConnected, fetchedAt }) {
  const lines = [
    `${t('ui:connectionHealth.label')}: ${t(`ui:connectionHealth.${status}`)}`,
  ];
  const allComponents = {
    ...(components || {}),
    tagSocket: tagSocketConnected ? 'up' : 'down',
  };
  for (const [key, state] of Object.entries(allComponents)) {
    if (state === 'disabled') continue;
    const labelKey = COMPONENT_LABEL_KEYS[key];
    const label = labelKey ? t(labelKey) : key;
    const stateLabel = t(`ui:connectionHealth.componentState.${state}`);
    lines.push(`• ${label}: ${stateLabel}`);
  }
  if (fetchedAt) {
    const ageSec = Math.max(0, Math.round((Date.now() - fetchedAt) / 1000));
    lines.push(t('ui:connectionHealth.lastChecked', { seconds: ageSec }));
  }
  return lines.join('\n');
}

export default function ConnectionHealthPill() {
  const { t } = useTranslation(['ui']);
  const health = useConnectionHealth();
  const safeStatus = DOT_COLORS[health.status] ? health.status : 'disconnected';
  const tooltip = buildTooltip(t, { ...health, status: safeStatus });
  const label = t(`ui:connectionHealth.${safeStatus}`);

  return (
    <div
      className="inline-flex items-center px-2 py-1 rounded-full bg-gray-50 text-xs text-gray-700"
      role="status"
      aria-label={tooltip}
      title={tooltip}
    >
      <span
        data-testid="connection-pill-dot"
        className={`h-2 w-2 rounded-full mr-2 ${DOT_COLORS[safeStatus]}`}
        aria-hidden="true"
      />
      <span className="hidden md:inline">{label}</span>
    </div>
  );
}
